import * as React from "react";
import {
  DerivedDataObject,
  Filter
} from "react-table";
import styled from "styled-components";

import { PaginationButtons } from "../Button";
import { Select } from "../Forms";
import { Text } from "../Text";

interface IPaginationProps {
  tableOf?: string;
  manual: boolean;
  filtered: Filter[];
  sortedData: DerivedDataObject[];
  page: number;
  pages: number;
  pageSize: number;
  pageSizeOptions: number[];
  totalRows: number;
  nextText: string;
  previousText: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export interface ITablePaginationRefProps {
  onPageChange: (page: number) => void;
  currentPage: number;
}

const TablePaginationStyled = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: top;
  padding-top: ${({ theme }) => theme.spacing.padding.sm};
  padding-left: ${({ theme }) => theme.spacing.padding.sm};
  min-height: 42px;

  .showing {
    margin: 0;
    padding: 6px 0 0;
    font-size: ${({ theme }) => theme.fonts.sizes.h4};
  }

  .paginator-container {
    display: flex;
    flex-direction: row;
    align-items: top;
    position: relative;

    .next-prev,
    .paginators {
      margin: 0;
      padding: 0;

      li {
        margin: 0;
        padding: 0;
      }
    }

    .select-container {
      padding-left: ${({ theme }) => theme.spacing.padding.sm};

      > div {
        margin: 0;
      }

      label {
        display: none;
      }

      select {
        margin: 0;
        height: 32px;
        line-height: 1em;
      }
    }
  }
`;

const TablePagination: React.ForwardRefRenderFunction<ITablePaginationRefProps, IPaginationProps> = (props, ref) => {
  const {
    manual,
    filtered,
    sortedData,
    page,
    pages,
    totalRows,
    pageSizeOptions,
    pageSize,
    nextText,
    previousText,
    onPageChange,
    onPageSizeChange
  } = props;

  const isFiltered = filtered.length > 0;
  let { tableOf } = props;
  let totalRowsCount = totalRows;
  let pageNum = 0;
  let pagesCount = 0;
  let currentAmount = 0;
  let ofAmount = pageSize;
  let totalRowsCountFormatted = "0";
  let showText = "";

  // Set tableOf default
  tableOf = tableOf || "rows";

  // Set manual is false and the table has been filtered, use the the totalRowsCount accordingly
  if (!manual && isFiltered) {
    totalRowsCount = sortedData.length;
  }

  // Set pageNum, pagesCount and totalRowsCountFormatted
  pageNum = totalRowsCount > 0 ? page + 1 : 0;
  pagesCount = totalRowsCount > 0 ? pages : 0;
  totalRowsCountFormatted = new Intl.NumberFormat("en-GB").format(totalRowsCount);
  currentAmount = pageSize * (pageNum - 1);
  ofAmount = pageSize * (pageNum - 1) + pageSize;

  // Set the human readable text accordingly
  if (pagesCount > 1) {
    showText = `Showing ${currentAmount + 1} - ${totalRowsCount < ofAmount ? totalRowsCount : ofAmount} of ${totalRowsCountFormatted}`;
  } else if (pagesCount === 1 && filtered.length === 0) {
    showText = `Showing all ${totalRowsCountFormatted}`;
  } else {
    showText = `Showing ${totalRowsCountFormatted}`;
  }

  // Alter the human readable text if a filter is applied
  if (filtered.length > 0) {
    showText = `${showText} filtered ${tableOf}.`;
  } else {
    showText = `${showText} ${tableOf}.`;
  }

  React.useImperativeHandle<ITablePaginationRefProps, ITablePaginationRefProps>(
    ref,
    () => ({
      onPageChange: page => {
        onPageChange(page);
      },
      currentPage: page
    }),
    [ onPageChange, page ]
  );

  return (
    <TablePaginationStyled className="rt-pagination">
      <Text
        as="p"
        className="showing"
        fontSize="h4"
      >
        {showText}
      </Text>

      <div className="paginator-container">
        {pagesCount > 1 && (
          <PaginationButtons
            pages={pagesCount}
            page={pageNum}
            buttons={{
              previous: {
                title: previousText,
                onClick: () => onPageChange(page - 1)
              },
              next: {
                title: nextText,
                onClick: () => {
                  onPageChange(page + 1);
                }
              },
              items: Array(pagesCount)
                .fill(pagesCount)
                .map((_p, i) => {
                  return {
                    title: `${i + 1}`,
                    onClick: () => onPageChange(i)
                  };
                })
            }}
          />
        )}

        <div className="select-container">
          <Select
            as="select"
            name="per-page"
            label="/ page"
            options={pageSizeOptions.map(sizeOpt => {
              return {
                checked: pageSize === sizeOpt,
                text: `${sizeOpt} / page`,
                value: `${sizeOpt}`
              };
            })}
            value={pageSize.toString()}
            handleChange={event => onPageSizeChange(parseInt(event.currentTarget.value, 0))}
          />
        </div>
      </div>
    </TablePaginationStyled>
  );
};

export default React.forwardRef(TablePagination);
